import H from '@here/maps-api-for-javascript/bin/mapsjs.bundle.harp'
import { mapConfig } from 'lib/config'

import { createPlatform } from './platform'

export type DefaultLayers = Record<string, Record<string, Record<string, H.map.layer.Layer>>>

// Modules singleton instance of platform Layers
let platformLayers: DefaultLayers | undefined = undefined

export enum BaseLayerNames {
  OMV = 'omv',
  SATELLITE = 'satellite',
  NORMAL = 'normal',
  SATELLITE_TRAFFIC = 'satellite_traffic',
  TRAFFIC = 'traffic',
}

export const TrafficLayerName = 'traffic'

const {
  layers: {
    raster: { satellite: satelliteConfig },
  },
} = mapConfig

export function getMapLayerName(
  baseLayer: BaseLayerNames
): BaseLayerNames.OMV | BaseLayerNames.SATELLITE {
  switch (baseLayer) {
    case BaseLayerNames.OMV:
    case BaseLayerNames.NORMAL:
    case BaseLayerNames.TRAFFIC:
      return BaseLayerNames.OMV
    case BaseLayerNames.SATELLITE:
    case BaseLayerNames.SATELLITE_TRAFFIC:
      return BaseLayerNames.SATELLITE
  }
}

// WEGO-10170: Ensure language is supported by satellite service (RTS).
function getSupportedLanguage(language: string): string | undefined {
  if (satelliteConfig.languages.includes(language)) {
    return language
  }
  // Try again checking that language code is lower case and removing suffixes (e.g. en-US -> en).
  // Otherwise use default language.
  const altLanguage = language.toLowerCase().split('-')[0]
  return satelliteConfig.languages.includes(altLanguage)
    ? altLanguage
    : mapConfig.layers.defaultLanguage
}

// TODO we need to find out how type, tileType, tileScheme, and tileFormat can be set in layers configuration
export const createLayers = (
  dpr: number,
  language: string,
  countryCode: string | null
): DefaultLayers => {
  if (platformLayers !== undefined) {
    return platformLayers
  }

  const { tileSizes, ppis } = satelliteConfig
  const isHighRes = dpr > 1
  const tileSize = isHighRes ? tileSizes.highRes : tileSizes.lowRes
  const ppi = isHighRes ? ppis.highRes : ppis.lowRes
  const engineType = H.Map.EngineType['HARP']
  const countryCodeUpperCase = countryCode?.toUpperCase()
  // WEGO-10170: RTS service returns 400 if political view or language does not exist, check the values
  // against the supported options defined in configuration.
  const politicalView = satelliteConfig.politicalViews.includes(countryCodeUpperCase ?? '')
    ? countryCodeUpperCase
    : undefined
  const mapTypes = createPlatform().createDefaultLayers({
    engineType: engineType,
    lg: getSupportedLanguage(language),
    lg2: mapConfig.layers.defaultLanguage,
    politicalView,
    ppi,
    tileSize,
  }) as unknown as DefaultLayers
  platformLayers = mapTypes

  return platformLayers
}

export const createHindiLayers = (): {
  omvLayer: H.map.layer.Layer
  satelliteLayer: H.map.layer.Layer
} => {
  const isHighRes = window.devicePixelRatio > 1
  const ppi = isHighRes ? 400 : 100
  const omvTileService = createPlatform().getRasterTileService({
    format: 'png8',
    queryParams: { lang: 'hi', ppi, style: 'explore.day' },
    resource: 'base',
  })
  const omvTileRasterTileProvider = new H.service.rasterTile.Provider(omvTileService, {
    engineType: H.Map.EngineType.HARP,
  })
  const omvLayer = new H.map.layer.TileLayer(omvTileRasterTileProvider)
  const satelliteTileService = createPlatform().getRasterTileService({
    format: 'png8',
    queryParams: { lang: 'hi', ppi, style: 'explore.satellite.day' },
    resource: 'base',
  })
  const satelliteTileRasterTileProvider = new H.service.rasterTile.Provider(satelliteTileService, {
    engineType: H.Map.EngineType.HARP,
  })
  const satelliteLayer = new H.map.layer.TileLayer(satelliteTileRasterTileProvider)

  return { omvLayer, satelliteLayer }
}
